import React, { useState, useMemo, useEffect } from 'react';
import { FaFilter } from 'react-icons/fa';
import SearchBar from '@components/Layout/SearchBar';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import DataLoader from '@components/DataLoader';
import Swal from 'sweetalert2';
import { useAuditLog } from '../../contexts/AuditLogContext';
import AuditLogDisplay from './AuditLogDisplay';

const MySwal = Swal;

const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
};

type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

// type UserData = {
//     name: string;
//     deviceId: string;
//     email: string;
//     mobileNo: string;
//     userCreateDateString: string;
// };

const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn !bg-primary py-2 !text-white"
    >
        {children}
        <FaFilter className="ml-2" /> {/* Add your custom icon here */}
    </button>
));

interface UserData {
    id: string;
    email?: string;
    name?: string;
    deviceId?: string;
    mobileNo?: string;
    createdAt?: string;
    createdAt_time?: string;
    hospitalName?: string;
    hospitalContact?: string;
    status?: string;
    userEmailStatus?: string;
}

const AuditLogList = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterField, setFilterField] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState<UserData[]>([]);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [buttonText, setButtonText] = useState('Add Individual');
    const { logs, fetchLogs, hasMore } = useAuditLog();

    useEffect(() => {
        fetchLogs();
    }, []);

    const [tableHeading, setTableHeading] = useState([
        {
            id: 1,
            title: t('audit_log_table.action'),
        },
        {
            id: 2,
            title: t('audit_log_table.host'),
        },
        {
            id: 3,
            title: t('audit_log_table.model_type'),
        },
        {
            id: 4,
            title: t('audit_log_table.user'),
        },
        {
            id: 5,
            title: t('audit_log_table.time_stamp'),
        },
        // Add more user objects here
    ]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSortChange = (event: string) => {
        setSortField(event);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const filteredUsers = useMemo(() => {
        let filtered = logs;

        // if (searchTerm) {
        //     filtered = filtered.filter(
        //         (user) =>
        //             user.name
        //                 ?.toLowerCase()
        //                 .includes(searchTerm.toLowerCase()) ||
        //             user.deviceId
        //                 ?.toLowerCase()
        //                 .includes(searchTerm.toLowerCase()) ||
        //             user.email
        //                 ?.toLowerCase()
        //                 .includes(searchTerm.toLowerCase()) ||
        //             user.mobileNo
        //                 ?.toLowerCase()
        //                 .includes(searchTerm.toLowerCase())
        //     );
        // }

        return filtered;
    }, [searchTerm, filterField, statusFilter, logs]);

    const sortedData = useMemo(() => {
        let sorted = [...logs];

        sorted.sort((a: any, b: any) => {
            const dateA = parseDate(a.timestamp);
            const dateB = parseDate(b.timestamp);

            if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                return dateB.getTime() - dateA.getTime();
            }

            if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                return 1;
            }
            if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
                return -1;
            }

            return 0;
        });

        return sorted;
    }, [logs]);

    const handleLoadMore = () => {
        fetchLogs(true);
    };

    return (
        <>
            <div className="2xl:min-w-full xl:min-w-[1030px] lg:min-w-[745px]  mx-auto">
                <div className="flex justify-content-between align-items-center mb-5">
                    <h1 className="text-lg font-semibold">
                        {t('audit_log_table.title')}
                    </h1>
                </div>
                <AuditLogDisplay data={sortedData} header={tableHeading} />
                {hasMore && (
                    <div className="text-center mt-4">
                        <button onClick={handleLoadMore} className="btn btn-primary">
                            {t('load_more')}
                        </button>
                    </div>
                )}
            </div>
            {loading && <DataLoader />}
        </>
    );
};

export default AuditLogList;
